import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from '@material-ui/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import Tabs from '@material-ui/core/Tabs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import objectSupport from 'dayjs/plugin/objectSupport';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import PropTypes from 'prop-types';
import randomString from 'random-string';
import NumberFormat from 'react-number-format';
import Draggable from 'react-draggable';
import { v4 as uuidv4 } from 'uuid';

import {
    Title,
    Text,
    Avatar,
    GridContainer,
    GridItem,
    Tag,
    ContainedButton,
    PatientDetailTab,
    FileIcon,
    OutlinedButton,
    CallController,
    QuickNoteTag,
    StyledCard,
    StyledCardContent,
    Progress,
    ConfirmDialog,
    TextField,
    HorizontalRule,
    Span,
    KeyboardDatePicker,
    TimePicker,
    StyledFormHelperText,
    Input,
    Checkbox,
    OverlapCard,
    StyledDialog,
    IconButton,
    CrossIcon,
    Tooltip,
    SelectedActivityCard,
    ConfirmDeleteActivityDialog,
    DuplicateActivityDialog,
    ActivityDialog,
} from '../../components/index';

import { StyledBackground } from '../../components/MainLayout/style';

import MedicalRecord from '../../components/Provider/Session/MedicalRecord';
import MindGymSelectHomework from '../../components/Provider/Session/MindGymSelectHomework';
import ProviderNote from '../../components/Provider/Session/ProviderNote';

import Main from '../../components/VideoCall/main';

import {
    GET_SESSION,
    GET_PROVIDER_TAGS,
    CREATE_PROVIDER_TAG,
    GET_EMOTIONS,
    UPDATE_PATIENT_SUMMARY,
    UPDATE_SESSION,
    UPDATE_PATIENT,
    GET_CALENDAR,
    GET_WORKOUT,
    UPDATE_WORKOUT,
    addSessionNote,
    updateSessionNote,
    deleteSessionNote,
    endOneOnOneSession,
} from '../../services/provider';

import {
    alertError,
    alertSuccess,
    // setExpressions,
} from '../../redux/stateActions';

import socket from '../../socket';

import { ACTIVITIES, DEFAULT_WORKOUT } from '../../constant';

import { minutesToMilliseconds } from '../../utils/mindgym';

dayjs.extend(isSameOrAfter);
dayjs.extend(objectSupport);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);

const STATUS = Object.freeze({
    CALLING: 'CALLING',
    SUMMARY: 'SUMMARY',
    END: 'END',
});

const mindgymDateFormat = 'YYYY-MM-DD';

const formatStartDateToMindgyn = (dateTime) => {
    if (!dateTime) return null;
    return dayjs(dateTime).startOf('day').utc().format();
};
const formatEndDateToMindgyn = (dateTime) => {
    if (!dateTime) return null;
    return dayjs(dateTime).endOf('day').utc().format();
};

const Call = ({ history, location }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    // const expressions = useSelector((state) => state.expressions);

    // DEBUG
    const [sessionStatus, setSessionStatus] = useState(STATUS.CALLING);
    // const [sessionStatus, setSessionStatus] = useState(STATUS.SUMMARY);

    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedRightTab, setSelectedRightTab] = useState(0);

    const [patientData, setPatientData] = useState({});

    const [isDirty, setIsDirty] = useState(false);
    const [medicalRecordData, setMedicalRecordData] = useState({
        mse: '',
        diagnosis: '',
        tag: [],
        treatment: '',
        noteToPsychiatrist: '',
    });

    const [patientSummary, setPatientSummary] = useState('');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // useEffect(() => {
    //     console.log('patientSummary', patientSummary);
    // }, [patientSummary]);

    /*--------------- Call Control Controller ---------------*/
    const roomInitRef = useRef();

    /*--------------- Get Emotions ---------------*/
    const [emotions, setEmotions] = useState([]);
    const { data: getEmotionsData, error: getEmotionsError } = useQuery(GET_EMOTIONS);
    useEffect(() => {
        if (getEmotionsError) {
            dispatch(alertError(`Cannot Get Emotions: ${getEmotionsError.message}`));
        }
        if (getEmotionsData && !_.isEmpty(getEmotionsData.emotions)) {
            setEmotions(getEmotionsData.emotions);
        }
    }, [getEmotionsError, getEmotionsData]);

    /*--------------- Get Session ---------------*/
    const [actualStartTime, setActualStartTime] = useState(null);
    const [actualEndTime, setActualEndTime] = useState(null);
    const {
        refetch: getSessionRefetch,
        data: getSessionData,
        loading: getSessionLoading,
        error: getSessionError,
    } = useQuery(GET_SESSION, {
        variables: {
            id,
        },
        // DEBUG
        skip: !id,
        // skip: true,
    });

    useEffect(() => {
        if (getSessionError) {
            dispatch(alertError(`Cannot Get Session: ${getSessionError.message}`));
            history.push('/provider/session');
        }
        if (getSessionData && !_.isEmpty(getSessionData.session)) {
            console.log('getSessionData.session', getSessionData.session);
            // check if session end
            if (getSessionData.session.cancelled) {
                dispatch(alertError(`Session Cancelled`));
                history.push('/provider/session');
                return;
            }
            if (getSessionData.session.sessionEnd) {
                dispatch(alertSuccess(`Session Ended`));
                history.push('/provider/session');
                return;
            }

            // check if video call end
            if (getSessionData.session.actualEndTime) {
                setSessionStatus(STATUS.SUMMARY);
                // setSelectedTab(0);

                setActualEndTime(dayjs(getSessionData.session.actualEndTime));
            }

            // check if start call for firstTime
            if (!getSessionData.session.actualStartTime) {
                updateSession({
                    variables: {
                        id: id,
                        actualStartTime: dayjs(),
                    },
                });
                setActualStartTime(dayjs());
            } else {
                setActualStartTime(dayjs(getSessionData.session.actualStartTime));
            }

            setPatientSummary(getSessionData.session.patient_summaries[0]);

            const {
                MSE,
                diagnosis,
                noteToPatient,
                treatment,
                noteToPsychiatrist,
                homework,
                patient,
                mindgymHomework,
                nextAppointment,
            } = getSessionData.session.patient_summaries[0];

            setPatientData(patient);

            if (isDirty) return;

            setMedicalRecordData({
                mse: MSE || '',
                diagnosis: diagnosis || '',
                tag: patient.patientTag,
                treatment: treatment || '',
                noteToPsychiatrist: noteToPsychiatrist || '',
            });

            setSummaryData({
                note: noteToPatient || '',
                homework: homework || '',
            });

            // only update session notes if not already exit prevent rerender
            if (_.isEmpty(notes)) {
                const newSessionNotes = Array.from(getSessionData.session.session_notes).sort(
                    (a, b) => (dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1)
                );

                setNotes(newSessionNotes);
            }

            const { name, description, startTime, endTime } = getSessionData.session;
            setName(name);
            setDescription(description);

            setAppointmentStartTime(
                dayjs()
                    .set({
                        hour: startTime.split(':')[0],
                        minute: startTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            setAppointmentEndTime(
                dayjs()
                    .set({
                        hour: endTime.split(':')[0],
                        minute: endTime.split(':')[1],
                    })
                    .format('HH:mm')
            );

            if (!_.isEmpty(mindgymHomework)) {
                setDateAndPeriodData(mindgymHomework.dateAndPeriodData);
                setSelectedActivity(mindgymHomework.selectedActivity);
            }

            if (!_.isEmpty(nextAppointment)) {
                setNoAppointment(nextAppointment.noAppointment);
                setName(nextAppointment.name);
                setDescription(nextAppointment.description);
                setAppointmentDate(nextAppointment.appointmentDate);
                setAppointmentStartTime(nextAppointment.appointmentStartTime);
                setAppointmentEndTime(nextAppointment.appointmentEndTime);
            }

            setIsDirty(true);
        }
    }, [getSessionError, getSessionData]);

    /*--------------- Update Patient Summary ---------------*/
    const [
        updatePatientSummary,
        { data: updatePatientSummaryData, error: updatePatientSummaryError },
    ] = useMutation(UPDATE_PATIENT_SUMMARY);

    useEffect(() => {
        if (updatePatientSummaryError) {
            dispatch(
                alertError(`Cannot update Client Summary: ${updatePatientSummaryError.message}`)
            );
        }
        if (updatePatientSummaryData) {
            console.log('updatePatientSummaryData', updatePatientSummaryData);
            // getSessionRefetch();
        }
    }, [updatePatientSummaryError, updatePatientSummaryData]);

    const [timer, setTimer] = useState(0);
    const [counting, setCounting] = useState(false);

    const startTimer = () => {
        setTimer(5);
        setCounting(true);
    };

    // Auto save when provider idle after typing 10 second
    useEffect(() => {
        let myInterval = setInterval(() => {
            if (timer > 0 && counting) {
                setTimer(timer - 1);
            }
            if (timer === 0 && counting) {
                setCounting(false);
                clearInterval(myInterval);
                handleSave();
            }
        }, 1000);
        return () => {
            clearInterval(myInterval);
        };
    }, [timer]);

    const handleSave = () => {
        console.log('updatePatientSummary');
        // DEBUG
        updatePatientSummary({
            variables: {
                id: patientSummary.id,
                MSE: medicalRecordData.mse,
                diagnosis: medicalRecordData.diagnosis,
                treatment: medicalRecordData.treatment,
                noteToPsychiatrist: medicalRecordData.noteToPsychiatrist,
                noteToPatient: summaryData.note,
                homework: summaryData.homework,
                mindgymHomework: createMindgymHomework(true),
                nextAppointment: {
                    noAppointment,
                    name,
                    description,
                    appointmentDate,
                    appointmentStartTime,
                    appointmentEndTime,
                },
            },
        });
    };

    /*--------------- Summary Sesssion ---------------*/
    const [errors, setErrors] = useState([]);
    const [summaryData, setSummaryData] = useState({
        note: '',
        homework: '',
    });

    const handleSummaryDataChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newData = { ...summaryData };
        newData[name] = value;

        setSummaryData(newData);
        startTimer();
    };

    /*--------------- Next Appointment ---------------*/
    const [noAppointment, setNoAppointment] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentStartTime, setAppointmentStartTime] = useState('');
    const [appointmentEndTime, setAppointmentEndTime] = useState('');

    const handleSetNoAppointment = (value) => {
        setNoAppointment(value);
        setErrors([]);
        startTimer();
    };

    const handleDateChange = (date) => {
        setAppointmentDate(date);
        setErrors(errors.filter((error) => error.property !== 'appointmentDate'));
        startTimer();
    };

    const handleStartTimeChange = (time) => {
        setAppointmentStartTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentEndTime' &&
                    error.property !== 'appointmentStartTime'
            )
        );
        startTimer();
    };
    const handleEndTimeChabge = (time) => {
        setAppointmentEndTime(time);
        setErrors(
            errors.filter(
                (error) =>
                    error.property !== 'appointmentEndTime' &&
                    error.property !== 'appointmentStartTime'
            )
        );
        startTimer();
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'name'));
        startTimer();
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
        setErrors(errors.filter((error) => error.property !== 'description'));
        startTimer();
    };

    /*--------------- Get Calendar ---------------*/
    const [calendar, setCalerdar] = useState({});
    const [overlapData, setOverlapData] = useState({});

    const {
        data: getCalendarData,
        error: getCalendarError,
        loading: getCalendarLoading,
    } = useQuery(GET_CALENDAR, {
        variables: {
            staff: user.staff.id,
            date: !_.isNull(appointmentDate) && dayjs(appointmentDate).format(mindgymDateFormat),
        },
        skip:
            _.isEmpty(user) ||
            _.isNull(appointmentDate) ||
            !user?.staff?.id ||
            sessionStatus !== STATUS.SUMMARY,
    });

    useEffect(() => {
        if (getCalendarError) {
            dispatch(alertError(`Cannot get calendar: ${getCalendarError.message}`));
        }

        if (getCalendarData && getCalendarData.calendars.length > 1) {
            dispatch(alertError(`More than one calendar found`));
            return;
        }

        if (getCalendarData) {
            if (_.isEmpty(getCalendarData.calendars)) {
                setCalerdar({});
            } else {
                setCalerdar(getCalendarData.calendars[0]);
            }
        }
    }, [getCalendarData, getCalendarError]);

    const onClear = () => {
        setSummaryData({
            note: '',
            homework: '',
        });

        setMedicalRecordData({
            mse: '',
            diagnosis: '',
            tag: [],
            treatment: '',
            noteToPsychiatrist: '',
        });

        setName('');
        setDescription('');
        setAppointmentDate(null);
        setAppointmentStartTime('');
        setAppointmentEndTime('');
    };

    const onClickSave = () => {
        if (!noAppointment) {
            const newErrors = [];

            if (name === '') {
                newErrors.push({
                    property: 'name',
                    message: 'Please provide a valid Name.',
                });
            }

            if (description === '') {
                newErrors.push({
                    property: 'description',
                    message: 'Please provide a valid Description.',
                });
            }

            if (appointmentDate === null) {
                newErrors.push({
                    property: 'appointmentDate',
                    message: 'Please provide a valid Date.',
                });
            }

            if (appointmentStartTime === '') {
                newErrors.push({
                    property: 'appointmentStartTime',
                    message: 'Please provide a valid Start Time.',
                });
            }

            if (appointmentEndTime === '') {
                newErrors.push({
                    property: 'appointmentEndTime',
                    message: 'Please provide a valid End Time.',
                });
            }

            if (
                dayjs()
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isSameOrAfter(
                        dayjs().set({
                            hour: appointmentEndTime.split(':')[0],
                            minute: appointmentEndTime.split(':')[1],
                        }),
                        'minute'
                    )
            ) {
                newErrors.push({
                    property: 'appointmentEndTime',
                    message: 'End Time must be after Start Time.',
                });
            }

            if (
                appointmentDate !== null &&
                (dayjs(appointmentDate)
                    .set({
                        hour: appointmentStartTime.split(':')[0],
                        minute: appointmentStartTime.split(':')[1],
                    })
                    .isSameOrBefore(dayjs()) ||
                    dayjs(appointmentDate)
                        .set({
                            hour: appointmentEndTime.split(':')[0],
                            minute: appointmentEndTime.split(':')[1],
                        })
                        .isSameOrBefore(dayjs()))
            ) {
                newErrors.push({
                    property: 'appointmentEndTime',
                    message: 'Session must be in the future.',
                });
            }

            if (!_.isEmpty(newErrors)) {
                return setErrors(newErrors);
            }

            if (!noAppointment && !dayjs(appointmentDate).isValid()) {
                return;
            }

            if (!_.isEmpty(calendar)) {
                const { meets } = calendar;

                const startTimeOverlap = meets?.find((meet) =>
                    dayjs()
                        .set({
                            hour: appointmentStartTime.split(':')[0],
                            minute: appointmentStartTime.split(':')[1],
                        })
                        .isBetween(
                            dayjs().set({
                                hour: meet.startTime.split(':')[0],
                                minute: meet.startTime.split(':')[1],
                            }),
                            dayjs().set({
                                hour: meet.endTime.split(':')[0],
                                minute: meet.endTime.split(':')[1],
                            }),
                            'minute',
                            '[]'
                        )
                );
                const endTimeOverlap = meets?.find((meet) =>
                    dayjs()
                        .set({
                            hour: appointmentEndTime.split(':')[0],
                            minute: appointmentEndTime.split(':')[1],
                        })
                        .isBetween(
                            dayjs().set({
                                hour: meet.startTime.split(':')[0],
                                minute: meet.startTime.split(':')[1],
                            }),
                            dayjs().set({
                                hour: meet.endTime.split(':')[0],
                                minute: meet.endTime.split(':')[1],
                            }),
                            'minute',
                            '[]'
                        )
                );

                if (startTimeOverlap) return setOverlapData(startTimeOverlap);

                if (endTimeOverlap) return setOverlapData(endTimeOverlap);
            }
        }

        setOpen(true);
    };

    const onClickCancel = () => {
        setOpen(false);
    };

    const onCancelConfirm = () => {
        setOverlapData({});
        onClickCancel();
    };

    const [openMindgymError, setOpenMindgymError] = useState(false);

    const onCloseMindgymError = () => {
        setOpenMindgymError(false);
    };

    const onClickConfirm = async () => {
        try {
            setLoading(true);
            setOpen(false);
            handleSave();

            const data = {
                sessionId: id,
                patientSummaryId: patientSummary.id,
                noteToPatient: summaryData.note,
                homework: summaryData.homework,
                mindgymHomework: createMindgymHomework(),
            };

            if (!noAppointment) {
                data.nextAppointment = {
                    hospital: user.staff.hospital.id,
                    staff: user.staff.id,
                    patient: patientData.id,
                    name: name.trim(),
                    description: description.trim(),
                    date: dayjs(appointmentDate).format(mindgymDateFormat),
                    startTime: dayjs()
                        .set('date', dayjs(appointmentDate).get('date'))
                        .set('hour', appointmentStartTime.split(':')[0])
                        .set('minute', appointmentStartTime.split(':')[1])
                        .format('HH:mm:ss.SSS'),
                    endTime: dayjs()
                        .set('date', dayjs(appointmentDate).get('date'))
                        .set('hour', appointmentEndTime.split(':')[0])
                        .set('minute', appointmentEndTime.split(':')[1])
                        .format('HH:mm:ss.SSS'),
                };
            }

            console.log('data', data);

            // DEBUG
            // return;

            const res = await endOneOnOneSession(data);

            console.log('res', res);

            if (res.status !== 200) {
                throw new Error(res);
            }

            setSessionStatus(STATUS.END);

            window.onbeforeunload = null;
            window.close();
        } catch (error) {
            setLoading(false);
            console.log('onClickConfirm: ', error.response);

            if (error.response.status === 424) {
                setOpenMindgymError(true);
            }
            dispatch(alertError(`Cannot Save: ${error.response?.data?.message}`));
        }
    };

    /*--------------- Resize window to fix tab width ---------------*/
    const [centerMaxHeight, setCenterMaxHeight] = useState(800);
    const [centerPaddingBottom, setCenterPaddingBottom] = useState(0);
    const [rightMaxHeight, setRightMaxHeight] = useState(800);
    const [tagContainerWidth, setTagContainerWidth] = useState(300);

    const handleResize = () => {
        let widthHeight = window.innerHeight;

        if (selectedRightTab === 0) {
            setRightMaxHeight(widthHeight - 300);
        } else {
            setRightMaxHeight(widthHeight - 165);
        }

        if (sessionStatus === STATUS.CALLING) {
            setCenterMaxHeight(widthHeight - 210);
        } else {
            if (widthHeight < 830) {
                setCenterPaddingBottom(80);
                setCenterMaxHeight(830 - 320);
            } else {
                setCenterPaddingBottom(50);
                setCenterMaxHeight(widthHeight - 320);
            }
        }

        setTagContainerWidth(window.innerWidth / 3);
    };

    useEffect(() => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 1);

        handleResize();
    }, []);

    useEffect(() => {
        handleResize();
    }, [selectedRightTab]);

    window.addEventListener('resize', handleResize);

    useEffect(() => {
        handleResize();
    }, [sessionStatus]);

    const handleMedicalRecordChange = (value) => {
        setMedicalRecordData(value);
        startTimer();
    };

    const tabsChangeHandler = (event, newValue) => {
        setSelectedTab(newValue);
    };
    const rightTabsChangeHandler = (event, newValue) => {
        setSelectedRightTab(newValue);
    };

    /*--------------- Update Session ---------------*/
    const [updateSession, { data: updateSessionData, error: updateSessionError }] =
        useMutation(UPDATE_SESSION);

    useEffect(() => {
        if (updateSessionError) {
            dispatch(alertError(`Cannot update Session: ${updateSessionError.message}`));
        }
        if (updateSessionData && !_.isEmpty(updateSessionData.updateSession)) {
            if (updateSessionData.updateSession.session.actualEndTime) {
                setSessionStatus(STATUS.SUMMARY);
                // setSelectedTab(0)

                setActualEndTime(dayjs(updateSessionData.updateSession.session.actualEndTime));
            }
        }
    }, [updateSessionError, updateSessionData]);

    /*--------------- End Call ---------------*/
    const endCallCallback = () => {
        socket.emit('providerEndOneCall', {
            sessionId: id,
            patientId: patientData.id,
        });

        updateSession({
            variables: {
                id: id,
                actualEndTime: dayjs(),
            },
        });
    };

    /*--------------- Get Provider Tags ---------------*/
    const [providerTags, setProviderTags] = useState([]);
    const {
        data: getProviderTagsData,
        error: getProviderTagsError,
        refetch: getProviderTagsRefetch,
    } = useQuery(GET_PROVIDER_TAGS, {
        variables: {
            id: user.staff.id,
        },
        skip: _.isEmpty(user),
    });
    useEffect(() => {
        if (getProviderTagsError) {
            dispatch(alertError(`Cannot Get Tags: ${getProviderTagsError.message}`));
        }
        if (getProviderTagsData) {
            setProviderTags(getProviderTagsData.providerTags);
        }
    }, [getProviderTagsError, getProviderTagsData]);

    /*--------------- Create Provider Tag ---------------*/
    const [createProviderTag, { data: createProviderTagData, error: createProviderTagError }] =
        useMutation(CREATE_PROVIDER_TAG);

    useEffect(() => {
        if (createProviderTagError) {
            dispatch(alertError(`Cannot Add quick note tag: ${createProviderTagError.message}`));
        }
        if (createProviderTagData) {
            getProviderTagsRefetch();
        }
    }, [createProviderTagError, createProviderTagData]);

    const handleAddProviderTag = (value) => {
        createProviderTag({
            variables: {
                id: user.staff.id,
                tag: value,
            },
        });
    };

    /*--------------- Note ---------------*/
    const [notes, setNotes] = useState([]);

    const handleSaveNote = async (value) => {
        try {
            let res;
            if (value.id) {
                res = await updateSessionNote(value.id, {
                    description: value.description,
                    emotions: value.emotions,
                    provider_tags: value.provider_tags,
                });
            } else {
                res = await addSessionNote({
                    session: id,
                    description: value.description,
                    emotions: value.emotions,
                    provider_tags: value.provider_tags,
                });
            }

            if (res.status === 200) {
                const newNotes = notes.slice();
                if (value.id) {
                    const findIndex = newNotes.findIndex((note) => note.id === value.id);
                    newNotes[findIndex] = res.data;
                } else {
                    const findIndex = newNotes.findIndex((note) => note.tempId === value.tempId);
                    newNotes[findIndex] = res.data;
                }
                setNotes(newNotes);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot save note: ${error}`));
        }
    };

    const handleDeleteNote = async (id) => {
        const newNotes = notes.slice();
        const foundIndex = newNotes.findIndex((note) => note.id === id);
        newNotes.splice(foundIndex, 1);

        setNotes(newNotes);

        if (id) {
            try {
                const res = await deleteSessionNote(id);

                if (res.status === 200) {
                    getSessionRefetch();
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot delete note: ${error}`));
            }
        }
    };

    const handleAddNewNote = async (tag) => {
        const newNotes = notes.slice();

        const noteObj = {
            session: id,
            description: '',
            emotions: [],
        };

        if (!tag) {
            // add empty note
            noteObj.newNote = true;
            noteObj.provider_tags = [];
            noteObj.tempId = randomString({ length: 24 }).toLowerCase();
            newNotes.unshift(noteObj);
            setNotes(newNotes);
        } else {
            // create new note with tag
            try {
                const res = await addSessionNote({ ...noteObj, provider_tags: [tag.id] });
                if (res.status === 200) {
                    noteObj.provider_tags = [tag];
                    noteObj.id = res.data.id;
                    newNotes.unshift(noteObj);
                    setNotes(newNotes);
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Cannot add note: ${error}`));
            }
        }
    };

    /*--------------- Update Patient ---------------*/
    const [updatePatient, { data: updatePatientData, error: updatePatientError }] =
        useMutation(UPDATE_PATIENT);

    useEffect(() => {
        if (updatePatientError) {
            dispatch(alertError(`Cannot update Client Tag: ${updatePatientError.message}`));
        }
        if (updatePatientData) {
            getSessionRefetch();
        }
    }, [updatePatientError, updatePatientData]);

    const handleAddTag = (value) => {
        updatePatient({
            variables: {
                userID: patientData?.user?.id,
                patientID: patientData?.id,
                patientTag: value,
            },
        });
    };

    useEffect(() => {
        socket.on('patientEndCall', (data) => {
            if (data.sessionId === id) {
                roomInitRef.current.clientLeaveRoom(endCallCallback);
            }
        });
    }, []);

    // const [expressTimer, setExpressTimer] = useState(0);
    // const [expressCounting, setExpressCounting] = useState(false);

    // const startExpressTimer = () => {
    //     setExpressTimer(5);
    //     setExpressCounting(true);
    // };

    // clear expressions after typing 5 second
    // useEffect(() => {
    //     let myInterval = setInterval(() => {
    //         console.log('expressTimer', expressTimer);
    //         if (expressTimer > 0 && expressCounting) {
    //             setExpressTimer(expressTimer - 1);
    //         }
    //         if (expressTimer === 0 && expressCounting) {
    //             setExpressCounting(false);
    //             clearInterval(myInterval);
    //             dispatch(setExpressions(null));
    //         }
    //     }, 1000);
    //     return () => {
    //         clearInterval(myInterval);
    //     };
    // }, [expressTimer]);

    // useEffect(() => {
    //     console.log('expressions', expressions);
    //     if (expressions !== null) {
    //         startExpressTimer();
    //     }
    // }, [expressions]);

    /*---------- Personal Contact Dialog ----------*/
    const [openContact, setOpenContact] = useState(false);

    const handleOpenContact = () => {
        setOpenContact(true);
    };

    const handleCloseContact = () => {
        setOpenContact(false);
    };

    /*---------- Possible Activity ----------*/
    const [possibleActivity, setPossibleActivity] = useState([]);

    useEffect(() => {
        console.log('possibleActivity', possibleActivity);
    }, [possibleActivity]);

    const handleSelectPossibleActivity = (value) => {
        let newPossibleActivity = JSON.parse(JSON.stringify(possibleActivity));

        if (newPossibleActivity.find((p) => p === value)) {
            newPossibleActivity = newPossibleActivity.filter((p) => p !== value);
        } else {
            newPossibleActivity.push(value);
        }
        setPossibleActivity(newPossibleActivity);
    };

    /*--------------- Date and Period ---------------*/
    const [dateAndPeriodData, setDateAndPeriodData] = useState({
        startDate: null,
        endDate: null,
        applyAll: false,
        untilNext: false,
        // startDate: '2023-09-01T04:31:00.000Z',
        // endDate: '2023-09-23T04:33:00.000Z',
        // applyAll: false,
        // untilNext: false,
    });

    // useEffect(() => {
    //     console.log('dateAndPeriodData', dateAndPeriodData);
    // }, [dateAndPeriodData]);

    const handleDateAndPeriodChange = (name, value) => {
        let newDateAndPeriodData = JSON.parse(JSON.stringify(dateAndPeriodData));

        newDateAndPeriodData[name] = value;

        if (name === 'untilNext' && appointmentDate) {
            newDateAndPeriodData.endDate = dayjs(appointmentDate).subtract(1, 'day');
        }

        if (
            name === 'endDate' &&
            newDateAndPeriodData.untilNext &&
            appointmentDate !== null &&
            !dayjs(value).isSame(dayjs(appointmentDate).subtract(1, 'day'), 'day')
        ) {
            newDateAndPeriodData['untilNext'] = false;
        }

        if (name === 'startDate') {
            newDateAndPeriodData['endDate'] = null;
        }

        setDateAndPeriodData(newDateAndPeriodData);

        startTimer();
    };

    /*--------------- Select Activity ---------------*/
    const [activityData, setActivityData] = useState({});
    const [activityInputValue, setActivityInputValue] = useState({});

    const [activeActivityDialog, setActiveActivityDialog] = useState({
        type: false,
        inputValue: {},
    });

    useEffect(() => {
        if (!activeActivityDialog.type) return;

        const { title, icon, description, defaultInputValue, selectInput, workoutInput } =
            JSON.parse(JSON.stringify(ACTIVITIES[activeActivityDialog.type]));
        setActivityData({
            title,
            icon,
            description,
            selectInput,
            workoutInput,
        });

        let newActivityInputValue;

        if (!_.isEmpty(activeActivityDialog?.inputValue)) {
            newActivityInputValue = activeActivityDialog?.inputValue;
        } else {
            newActivityInputValue = defaultInputValue;

            if (dateAndPeriodData.applyAll) {
                newActivityInputValue.startDate = dateAndPeriodData.startDate;
                newActivityInputValue.endDate = dateAndPeriodData.endDate;
            }
        }

        setActivityInputValue(newActivityInputValue);
    }, [activeActivityDialog]);

    // useEffect(() => {
    //     console.log('activityInputValue', activityInputValue);
    // }, [activityInputValue]);

    const handleClearActiveActivityDialog = () => {
        setActiveActivityDialog({ type: false, inputValue: {} });
    };

    const [selectedActivity, setSelectedActivity] = useState([]);
    const [duplicateActivity, setDuplicateActivity] = useState([]);

    // useEffect(() => {
    //     console.log('selectedActivity', selectedActivity);
    // }, [selectedActivity]);

    // useEffect(() => {
    //     console.log('duplicateActivity', duplicateActivity);
    // }, [duplicateActivity]);

    const handleClearDuplicateActivity = () => {
        setDuplicateActivity([]);
    };

    const [openDeleteActivity, setOpenDeleteActivity] = useState({});

    const handleClickEditActivity = (id) => {
        const foundActivity = selectedActivity.find((item) => item.id === id);

        if (!foundActivity) return;

        setActiveActivityDialog({ type: foundActivity.type, inputValue: foundActivity });
    };

    const handleClickDeleteActivity = (id, isDuplicate) => {
        setOpenDeleteActivity({ id, isDuplicate });
    };

    const onCancelDeleteActivity = () => {
        setOpenDeleteActivity({});
    };

    const onConfirmDeleteActivity = () => {
        setSelectedActivity((prev) => prev.filter((item) => item.id !== openDeleteActivity.id));
        setOpenDeleteActivity({});

        if (openDeleteActivity.isDuplicate) {
            setDuplicateActivity((prev) =>
                prev.filter((item) => item.id !== openDeleteActivity.id)
            );
        }

        startTimer();
    };

    const handleClickActivity = (type, isDuplicate) => {
        const alreadySelectedActivity = selectedActivity.filter((item) => item.type === type);

        if (!isDuplicate && !_.isEmpty(alreadySelectedActivity)) {
            return setDuplicateActivity(alreadySelectedActivity);
        }

        setActiveActivityDialog({ type: type, inputValue: {} });
    };

    const handleSaveActivity = (inputValue) => {
        console.log('handleSaveActivity', inputValue);

        setSelectedActivity((prev) => {
            const newSelectedActivity = JSON.parse(JSON.stringify(prev));

            if (inputValue.id !== null) {
                const foundIndex = newSelectedActivity.findIndex(
                    (item) => item.id === inputValue.id
                );

                newSelectedActivity[foundIndex] = inputValue;

                if (!_.isEmpty(duplicateActivity)) {
                    setDuplicateActivity((prevDuplicateActivity) => {
                        const newDuplicateActivity = JSON.parse(
                            JSON.stringify(prevDuplicateActivity)
                        );

                        const foundIndex = newDuplicateActivity.findIndex(
                            (item) => item.id === inputValue.id
                        );

                        newDuplicateActivity[foundIndex] = inputValue;

                        return newDuplicateActivity;
                    });
                }
            } else {
                const newMoodTracker = { ...inputValue, id: uuidv4() };
                newSelectedActivity.push(newMoodTracker);

                if (!_.isEmpty(duplicateActivity)) {
                    setDuplicateActivity((prevDuplicateActivity) => {
                        const newDuplicateActivity = JSON.parse(
                            JSON.stringify(prevDuplicateActivity)
                        );

                        newDuplicateActivity.push(newMoodTracker);

                        return newDuplicateActivity;
                    });
                }
            }

            return newSelectedActivity;
        });

        startTimer();
    };

    /*--------------- Get Workout ---------------*/
    const [workoutOptions, setWorkoutOptions] = useState(DEFAULT_WORKOUT);

    const { loading: getWorkoutLoading } = useQuery(GET_WORKOUT, {
        variables: {
            id: user.staff.id,
        },
        // DEBUG
        skip: _.isEmpty(user) || !user?.staff?.id || !patientData?.user?.uuid,
        // skip: _.isEmpty(user) || !user?.staff?.id,
        onCompleted: (res) => {
            console.log('GET_WORKOUT', res);

            if (!_.isEmpty(res?.staff?.workout)) {
                setWorkoutOptions(res.staff.workout);
            } else {
                setWorkoutOptions(DEFAULT_WORKOUT);
            }
        },
        onError: (res) => {
            dispatch(alertError(`Cannot get workout data: ${res.message}`));
        },
    });

    /*--------------- Update Workout ---------------*/
    const [updateWorkout] = useMutation(UPDATE_WORKOUT, {
        onCompleted: (res) => {
            console.log('UPDATE_WORKOUT', res);
        },
        onError: (res) => {
            dispatch(alertError(`Cannot update workout: ${res.message}`));
        },
    });

    const handleDeleteWorkout = (groupType, workoutIndex) => {
        const newWorkoutOptions = JSON.parse(JSON.stringify(workoutOptions));
        const foundIndex = newWorkoutOptions.findIndex((item) => item.type === groupType);
        const newGroup = newWorkoutOptions[foundIndex];
        newGroup.workout.splice(workoutIndex, 1);
        newWorkoutOptions[foundIndex] = newGroup;

        setWorkoutOptions(newWorkoutOptions);
        updateWorkout({
            variables: {
                id: user.staff.id,
                workout: newWorkoutOptions,
            },
        });
    };

    const handleCreateWorkout = (groupType, value) => {
        const newWorkoutOptions = JSON.parse(JSON.stringify(workoutOptions));
        const foundIndex = newWorkoutOptions.findIndex((item) => item.type === groupType);
        const newGroup = newWorkoutOptions[foundIndex];

        if (newGroup.workout.includes(value)) return;

        newGroup.workout.push(value);
        newWorkoutOptions[foundIndex] = newGroup;

        setWorkoutOptions(newWorkoutOptions);

        updateWorkout({
            variables: {
                id: user.staff.id,
                workout: newWorkoutOptions,
            },
        });
    };

    /*--------------- Process Homework ---------------*/
    const createMindgymHomework = useCallback(
        (draft) => {
            const obj = {};

            if (draft) {
                obj.dateAndPeriodData = dateAndPeriodData;
                obj.selectedActivity = selectedActivity;
            } else {
                if (_.isEmpty(selectedActivity)) return obj;

                obj.timeframe = {
                    start: formatStartDateToMindgyn(dateAndPeriodData.startDate),
                    end: formatEndDateToMindgyn(dateAndPeriodData.endDate),
                };

                obj.participant = {
                    patientId: patientData?.id,
                    staffId: user?.staff?.id,
                    hospitalId: user?.staff?.hospital?.id,
                    patientSummariesId: patientSummary?.id,
                    uuid: patientData?.user?.uuid,
                };
                obj.sessionId = id;

                const homework = [];

                selectedActivity.forEach((item) => {
                    const newHomework = {
                        type: ACTIVITIES[item.type].payloadType,
                        activityFrequency: item.frequency,
                        settings: {
                            startDate: formatStartDateToMindgyn(item.startDate),
                            endDate: formatEndDateToMindgyn(item.endDate),
                        },
                    };

                    switch (item.type) {
                        case 'BEHAVIORAL_TRACKER':
                            newHomework.settings = {
                                ...newHomework.settings,
                                goals: item.workout.map((ele) => {
                                    return {text: ele.split('#')[1], category: ele.split('#')[0]}
                                }),
                            };
                            break;
                        case 'BREATHING_EXERCISE':
                            newHomework.settings = {
                                ...newHomework.settings,
                                workoutOption: {
                                    category: item.workout,
                                    duration: minutesToMilliseconds(item.duration),
                                },
                            };
                            break;
                        case 'PROGRESSIVE_MUSCLE_RELAXATION':
                            newHomework.settings = {
                                ...newHomework.settings,
                                workoutOption: {
                                    duration: minutesToMilliseconds(item.duration),
                                },
                            };
                            break;
                        case 'MINDFULNESS':
                            newHomework.settings = {
                                ...newHomework.settings,
                                workoutOption: {
                                    category: item.workout,
                                    duration: minutesToMilliseconds(item.duration),
                                },
                            };
                            break;
                        default:
                            break;
                    }
                    homework.push(newHomework);
                });

                obj.homework = homework;
            }

            return obj;
        },
        [selectedActivity, dateAndPeriodData, patientData, patientSummary]
    );

    return sessionStatus === STATUS.END ? (
        <div
            style={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Text $bold>Session Ended please close this window</Text>
        </div>
    ) : (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: sessionStatus === STATUS.CALLING ? 'repeat(3,1fr)' : '1fr 2fr',
                minHeight: '100vh',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {(getSessionLoading || loading) && <Progress />}
            <ConfirmDialog
                open={openMindgymError}
                contentText="There's a temporary problem with service. Please try again or Remove Homework."
                cancelText="Close"
                onCancel={onCloseMindgymError}
            />
            <ConfirmDialog
                open={open}
                contentText="Confirm Save?"
                cancelText="Cancel"
                onCancel={onClickCancel}
                confirmText="Save"
                onConfirm={onClickConfirm}
            />
            <ConfirmDialog
                open={openContact}
                contentText={
                    <div>
                        <div style={{ display: 'flex', columnGap: '5px' }}>
                            <div>{`Contact Number:`}</div>
                            <NumberFormat
                                value={patientData.emergencyContractNumber}
                                format="###-###-####"
                                displayType={'text'}
                                renderText={(value, props) => <div {...props}>{value}</div>}
                            />
                        </div>
                        <div>{`Contact Name: ${patientData.emergencyContractName}`}</div>
                        <div>{`Relationship: ${patientData.emergencyContractRelation}`}</div>
                    </div>
                }
                cancelText="Close"
                onCancel={handleCloseContact}
            />
            <DuplicateActivityDialog
                open={!_.isEmpty(duplicateActivity)}
                onCancel={handleClearDuplicateActivity}
                duplicateActivity={duplicateActivity}
                onEdit={handleClickEditActivity}
                onDelete={handleClickDeleteActivity}
                handleClickActivity={handleClickActivity}
            />
            <ConfirmDeleteActivityDialog
                open={!_.isEmpty(openDeleteActivity)}
                onCancel={onCancelDeleteActivity}
                onConfirm={onConfirmDeleteActivity}
            />
            <ActivityDialog
                open={activeActivityDialog?.type ? true : false}
                data={activityData}
                selectedActivity={selectedActivity}
                inputValue={activityInputValue}
                setInputValue={setActivityInputValue}
                onCancel={handleClearActiveActivityDialog}
                onSave={handleSaveActivity}
                minDate={dateAndPeriodData?.startDate}
                maxDate={dateAndPeriodData?.endDate}
                workoutOptions={workoutOptions}
                handleDeleteWorkout={handleDeleteWorkout}
                handleCreateWorkout={handleCreateWorkout}
                loading={getWorkoutLoading}
                dateAndPeriodData={dateAndPeriodData}
            />
            {sessionStatus === STATUS.CALLING && (
                <div style={{ height: '100%', position: 'relative' }}>
                    <Main
                        ref={roomInitRef}
                        type="one"
                        isProvider={true}
                        faceDetection={true}
                        useMicCamera={true}
                    />
                    {/* <div
                        style={{
                            position: 'absolute',
                            bottom: '10px',
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '5px',
                        }}
                    >
                        <Text $color="pink" style={{ textTransform: 'capitalize' }}>
                            {expressions || ''}
                        </Text>
                    </div> */}
                </div>
            )}
            {sessionStatus === STATUS.SUMMARY && (
                <div
                    style={{
                        border: '1px solid white',
                        filter: 'drop-shadow(-4px 0px 8px rgba(174, 180, 210, 0.4))',
                        padding: '5px 20px',
                        position: 'relative',
                        maxHeight: '100vh',
                        overflowY: 'auto',
                    }}
                >
                    <StyledDialog
                        scroll="body"
                        open={!_.isEmpty(overlapData)}
                        onClose={onCancelConfirm}
                        aria-labelledby="warningDialog"
                        aria-describedby="warning dialog"
                        PaperProps={{ style: { borderRadius: '16px' } }}
                    >
                        <OverlapCard data={overlapData} onCancelConfirm={onCancelConfirm} />
                    </StyledDialog>
                    <StyledBackground />
                    <div>
                        <div>
                            <Text $fontSize="20px" $bold>
                                Session Summary
                            </Text>
                            <TextField
                                id="note"
                                name="note"
                                label="Note To Client"
                                placeholder="Note To Client"
                                minRows={3}
                                value={summaryData.note}
                                onChange={(e) => handleSummaryDataChange(e)}
                                helperText={
                                    errors.find((value) => value.property === 'note')?.message
                                }
                                inputProps={{ maxLength: 2000 }}
                            />
                            <TextField
                                id="homework"
                                name="homework"
                                label="Homework"
                                placeholder="Homework"
                                minRows={3}
                                value={summaryData.homework}
                                onChange={(e) => handleSummaryDataChange(e)}
                                helperText={
                                    errors.find((value) => value.property === 'homework')?.message
                                }
                                inputProps={{ maxLength: 2000 }}
                            />
                            {!_.isEmpty(selectedActivity) && (
                                <div style={{ margin: '5px 0' }}>
                                    <Text
                                        $fontSize="12px"
                                        $color="gray2"
                                        style={{ paddingLeft: '20px', marginBottom: '5px' }}
                                    >
                                        Friday Homework
                                    </Text>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            rowGap: '12px',
                                        }}
                                    >
                                        {selectedActivity.map((item, index) => (
                                            <SelectedActivityCard
                                                key={`selected-activity-${index}`}
                                                data={item}
                                                onEdit={() => handleClickEditActivity(item.id)}
                                                onDelete={() => handleClickDeleteActivity(item.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <HorizontalRule style={{ margin: '15px 0 20px' }} />
                        <div>
                            <Text $fontSize="20px" $bold>
                                Next Appointment <Span $color="gray2">(Optional)</Span>
                            </Text>
                            <Checkbox
                                label={
                                    <span style={{ marginLeft: '10px', fontSize: '14px' }}>
                                        No follow up
                                    </span>
                                }
                                checked={noAppointment}
                                name="noAppointment"
                                onChange={(e) => handleSetNoAppointment(e.target.checked)}
                            />
                            <KeyboardDatePicker
                                disablePast
                                id="newAppointmentDate"
                                label={
                                    <Span $color="black">
                                        Date <Span $color="pink">*</Span>
                                    </Span>
                                }
                                value={appointmentDate}
                                onChange={(date) => handleDateChange(date)}
                                disabled={noAppointment || loading}
                                helperText={
                                    errors.find((value) => value.property === 'appointmentDate')
                                        ?.message
                                }
                            />

                            <div style={{ display: 'flex', columnGap: '10px' }}>
                                <TimePicker
                                    id="startTime"
                                    label="Start time"
                                    required
                                    placeholder="Select"
                                    value={appointmentStartTime}
                                    onChange={handleStartTimeChange}
                                    disabled={noAppointment || loading}
                                    error={
                                        errors.find(
                                            (value) => value.property === 'appointmentStartTime'
                                        )?.message
                                    }
                                />

                                <TimePicker
                                    id="endTime"
                                    label="End time"
                                    required
                                    placeholder="Select"
                                    value={appointmentEndTime}
                                    onChange={handleEndTimeChabge}
                                    disabled={noAppointment || loading}
                                    error={
                                        errors.find(
                                            (value) => value.property === 'appointmentEndTime'
                                        )?.message
                                    }
                                />
                            </div>

                            <div>
                                {errors.find(
                                    (value) => value.property === 'appointmentStartTime'
                                ) && (
                                    <StyledFormHelperText error>
                                        {
                                            errors.find(
                                                (value) => value.property === 'appointmentStartTime'
                                            )?.message
                                        }
                                    </StyledFormHelperText>
                                )}
                                {errors.find(
                                    (value) => value.property === 'appointmentEndTime'
                                ) && (
                                    <StyledFormHelperText error>
                                        {
                                            errors.find(
                                                (value) => value.property === 'appointmentEndTime'
                                            )?.message
                                        }
                                    </StyledFormHelperText>
                                )}
                            </div>

                            <Input
                                label="Session Name"
                                required
                                placeholder="Session Name"
                                value={name}
                                onChange={handleNameChange}
                                helperText={
                                    errors.find((value) => value.property === 'name')?.message
                                }
                                disabled={noAppointment || loading}
                                max={100}
                            />

                            <TextField
                                required
                                id="description"
                                name="description"
                                label="Description"
                                placeholder="Description"
                                value={description}
                                onChange={handleDescriptionChange}
                                helperText={
                                    errors.find((value) => value.property === 'description')
                                        ?.message
                                }
                                disabled={noAppointment || loading}
                                inputProps={{ maxLength: 1000 }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div
                style={{
                    border: '1px solid white',
                    position: 'relative',
                }}
            >
                <StyledBackground />
                {sessionStatus === STATUS.CALLING && (
                    <div
                        style={{
                            background: theme.palette.common.shadeGrayGradient,
                            padding: '20px',
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                        }}
                    >
                        <div>
                            {!_.isEmpty(patientData) ? (
                                <Avatar
                                    alt={`${patientData?.user?.fullname}`}
                                    src={
                                        patientData?.user?.avatar
                                            ? patientData?.user?.avatar.url
                                            : null
                                    }
                                    $avatarSize="72px"
                                />
                            ) : (
                                <Skeleton variant="rect" width={72} height={72} />
                            )}
                        </div>
                        <div style={{ display: 'flex' }}>
                            <GridContainer style={{ paddingLeft: '10px' }}>
                                <GridItem justifyContent="flex-start">
                                    {!_.isEmpty(patientData) ? (
                                        <Title
                                            style={{
                                                fontSize: '16px',
                                            }}
                                            $ellipsis
                                            $lineHeight="20px"
                                        >
                                            {`${patientData?.user?.fullname}`}
                                        </Title>
                                    ) : (
                                        <Skeleton width={200} height={30} variant="text" />
                                    )}
                                </GridItem>
                                <GridItem
                                    justifyContent="flex-start"
                                    style={{
                                        alignSelf: 'flex-end',
                                        display: 'flex',
                                        columnGap: '5px',
                                    }}
                                >
                                    {!_.isEmpty(patientData.patientTag) &&
                                        patientData.patientTag
                                            .slice(0, 2)
                                            .map((tag) => <Tag key={tag}>{tag}</Tag>)}
                                </GridItem>
                            </GridContainer>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                rowGap: '10px',
                                justifyContent: 'center',
                            }}
                        >
                            <ContainedButton
                                $backgroundColor="green"
                                $height="auto"
                                onClick={handleOpenContact}
                            >
                                <Text $color="white" $bold>
                                    Personal
                                    <br />
                                    Contact
                                </Text>
                            </ContainedButton>
                        </div>
                    </div>
                )}

                {sessionStatus === STATUS.SUMMARY && (
                    <div
                        style={{
                            padding: '30px 24px 5px',
                            minWidth: '400px',
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: '15px',
                        }}
                    >
                        <StyledCard padding="10px 15px" style={{ maxWidth: '400px' }}>
                            <StyledCardContent>
                                <div
                                    style={{
                                        display: 'flex',
                                        columnGap: '10px',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div>
                                        {patientData ? (
                                            <Avatar
                                                alt={`${patientData?.user?.fullname}`}
                                                src={patientData?.avatar}
                                                $avatarSize="64px"
                                            />
                                        ) : (
                                            <Skeleton variant="rect" width={64} height={64} />
                                        )}
                                    </div>
                                    <div style={{ flexGrow: 1 }}>
                                        {patientData ? (
                                            <Title
                                                style={{
                                                    fontSize: '16px',
                                                }}
                                                $ellipsis
                                                $lineHeight="20px"
                                            >
                                                {`${patientData?.user?.fullname}`}
                                            </Title>
                                        ) : (
                                            <Skeleton width={200} height={30} variant="text" />
                                        )}
                                    </div>
                                    <div>
                                        <ContainedButton
                                            $icon
                                            onClick={() => console.log('view client detail')}
                                        >
                                            <FileIcon />
                                        </ContainedButton>
                                    </div>
                                </div>
                            </StyledCardContent>
                        </StyledCard>

                        <StyledCard padding="15px 20px" style={{ maxWidth: '600px' }}>
                            <StyledCardContent>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        rowGap: '10px',
                                    }}
                                >
                                    <Text $color="pink" $fontSize="14px">
                                        Consultation Time
                                    </Text>
                                    <Text $color="pink" $fontSize="14px">
                                        Provider
                                    </Text>
                                    {actualStartTime && actualEndTime && (
                                        <Text>{`${actualStartTime.format(
                                            'D MMM YYYY'
                                        )}, ${actualStartTime.format(
                                            'HH:mm'
                                        )}-${actualEndTime.format('HH:mm')} (${actualEndTime.diff(
                                            actualStartTime,
                                            'minute'
                                        )} min)`}</Text>
                                    )}
                                    <Text>{user.fullname}</Text>
                                </div>
                            </StyledCardContent>
                        </StyledCard>
                    </div>
                )}

                <Tabs
                    value={selectedTab}
                    onChange={tabsChangeHandler}
                    indicatorColor="primary"
                    TabIndicatorProps={{ style: { height: '4px' } }}
                    style={{
                        marginTop: '16px',
                        marginBottom: '16px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                    }}
                >
                    <PatientDetailTab label="Medical Record" $active={selectedTab === 0} />
                    {/* DEBUG */}
                    {sessionStatus === STATUS.SUMMARY &&
                        (patientData?.user?.uuid ? (
                            <PatientDetailTab label="Friday Homework" $active={selectedTab === 1} />
                        ) : (
                            <Tooltip
                                title="เนื่องจาก User รายนี้ไม่มี Account Friday app
จึงไม่สามารถใช้งาน Tab “Homework” ได้"
                                arrow
                            >
                                <div>
                                    <PatientDetailTab
                                        label="Friday Homework"
                                        $active={selectedTab === 1}
                                        disabled
                                    />
                                </div>
                            </Tooltip>
                        ))}
                </Tabs>
                <div
                    style={{
                        maxHeight: `${centerMaxHeight}px`,
                        overflowY: 'auto',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingBottom: centerPaddingBottom,
                    }}
                >
                    {selectedTab === 0 && (
                        <MedicalRecord
                            data={medicalRecordData}
                            onChange={handleMedicalRecordChange}
                            onAddTag={handleAddTag}
                        />
                    )}
                    {selectedTab === 1 && (
                        <MindGymSelectHomework
                            dateAndPeriodData={dateAndPeriodData}
                            handleDateAndPeriodChange={handleDateAndPeriodChange}
                            handleClickActivity={handleClickActivity}
                            noAppointment={noAppointment}
                            appointmentDate={appointmentDate}
                            selectedActivity={selectedActivity}
                        />
                    )}
                </div>

                {sessionStatus === STATUS.SUMMARY && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            columnGap: '20px',
                            backgroundColor: theme.palette.common.white,
                            opacity: 0.9,
                            border: `1px solid ${theme.palette.common.white}`,
                            boxSizing:
                                '0px -2px 4px rgba(174, 180, 210, 0.4), inset 0px -2px 4px rgba(174, 180, 210, 0.2)',
                            padding: '15px 24px',
                        }}
                    >
                        <OutlinedButton $width="90px" onClick={onClear}>
                            Clear
                        </OutlinedButton>
                        <ContainedButton
                            $width="90px"
                            onClick={onClickSave}
                            disabled={getCalendarLoading}
                        >
                            Save
                        </ContainedButton>
                    </div>
                )}
            </div>
            {sessionStatus === STATUS.CALLING && (
                <div
                    style={{
                        border: '1px solid white',
                        position: 'relative',
                    }}
                >
                    {/* <StyledBackground /> */}
                    <Tabs
                        value={selectedRightTab}
                        onChange={rightTabsChangeHandler}
                        indicatorColor="primary"
                        TabIndicatorProps={{ style: { height: '4px' }, children: <span /> }}
                        style={{
                            marginTop: '10px',
                            marginBottom: '16px',
                            paddingLeft: '20px',
                            paddingRight: '20px',
                        }}
                    >
                        <PatientDetailTab label="Take Note" $active={selectedRightTab === 0} />
                    </Tabs>

                    <div style={{ maxHeight: rightMaxHeight, overflowY: 'auto' }}>
                        {selectedRightTab === 0 && (
                            <ProviderNote
                                data={notes}
                                tags={providerTags}
                                emotions={emotions}
                                handleSaveNote={handleSaveNote}
                                handleDeleteNote={handleDeleteNote}
                            />
                        )}
                    </div>

                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            left: 0,
                        }}
                    >
                        {selectedRightTab === 0 && (
                            <QuickNoteTag
                                tagContainerWidth={tagContainerWidth}
                                onAddNote={handleAddNewNote}
                                onAddNewTag={handleAddProviderTag}
                                data={providerTags}
                                possibleActivity={possibleActivity}
                                handleSelectPossibleActivity={handleSelectPossibleActivity}
                            />
                        )}
                        <CallController
                            isProvider={true}
                            handleMic={() => roomInitRef.current.micControl()}
                            handleCamera={() => roomInitRef.current.webcamControl()}
                            onEndCall={() => {
                                try {
                                    roomInitRef.current.providerCloseRoom(endCallCallback);
                                } catch (error) {
                                    endCallCallback();
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {!_.isEmpty(possibleActivity) && sessionStatus === STATUS.SUMMARY && (
                <Draggable bounds="parent" cancel="button">
                    <StyledCard
                        $width="285px"
                        $backgroundColor="white"
                        padding="16px 0"
                        style={{
                            position: 'absolute',
                            zIndex: 100,
                            right: '40px',
                            bottom: '80px',
                            filter: 'drop-shadow(rgba(35, 15, 74, 0.16) 0px 0px 42px)',
                            cursor: 'pointer',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                paddingTop: '5px',
                                paddingRight: '9px',
                                marginRight: '2px',
                                top: 0,
                                right: 0,
                            }}
                        >
                            <IconButton
                                style={{ marginLeft: 'auto' }}
                                onClick={() => setPossibleActivity([])}
                            >
                                <CrossIcon width="24px" height="24px" fill="black" />
                            </IconButton>
                        </div>
                        <StyledCardContent>
                            <Text $fontSize="16px" $medium style={{ padding: '0 16px' }}>
                                Possible Activity
                            </Text>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                    marginTop: '5px',
                                }}
                            >
                                {possibleActivity.map((item, index) => (
                                    <div key={`activity_${index}`} style={{ padding: '0 16px' }}>
                                        <PossibleActivityItem type={item} />
                                        {possibleActivity.length !== index + 1 && (
                                            <div
                                                style={{
                                                    height: '1px',
                                                    background: theme.palette.common.gray6,
                                                    margin: '5px 0',
                                                }}
                                            ></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </StyledCardContent>
                    </StyledCard>
                </Draggable>
            )}
        </div>
    );
};

Call.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default Call;

const PossibleActivityItem = ({ type }) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                columnGap: '10px',
            }}
        >
            <Avatar
                alt={`logo ${ACTIVITIES[type].title}`}
                src={ACTIVITIES[type].icon}
                $avatarSize="24px"
            />
            <Text $fontSize="12px" style={{ flexGrow: 1 }}>
                {ACTIVITIES[type].title}
            </Text>
        </div>
    );
};
